import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog18 = () => {
  const hashtags = [
    "#selfsabotage",
    "#emotionalintelligence",
    "#selfawareness",
    "#personalgrowth",
    "#mindset",
    "#confidence",
    "#selfworth",
    "#healing",
    "#selfcompassion",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>10 Signs You’re Acting as Your Own Worst Enemy</title>
        <meta
          name="description"
          content="Discover 10 powerful signs you might be sabotaging your own growth — and how to stop acting as your own enemy."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-primary text-left text-3xl font-semibold mb-8">
          10 Signs You’re Acting as Your Own Worst Enemy
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog18.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        <p className="text-center italic text-gray-600 mb-6">
          The truth is — sometimes, the biggest battle isn’t with the world.
          It’s with yourself.
        </p>

        {/* Intro */}
        <section className="space-y-4 mb-8">
          <p>Let’s be honest.</p>

          <p>
            Most people think their life is hard because of other people — the
            boss, the partner, the economy, the system.
          </p>

          <p>
            But if we’re real for a moment…
            <br />
            the person who stops you most often
            <br />
            is the one staring back in the mirror.
          </p>

          <p>
            You say you want peace, yet you replay old pain.
            <br />
            You say you want success, yet you sabotage the good that comes your
            way.
            <br />
            You say you want love, yet you accept less than you deserve.
          </p>

          <p>
            We all do it.
            <br />
            Not because we’re broken — but because we’ve been conditioned to
            believe struggle is normal, and ease must be earned.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are 10 subtle ways you act as your own enemy — and how to
            finally stop.
          </p>
        </section>

        {/* Points */}
        <section className="space-y-6 mb-10">
          <div>
            <h3 className="text-primary font-semibold">
              1. You Downplay Your Achievements
            </h3>

            <p>
              You finish something big… and instantly say,
              <br />
              “Oh, it’s nothing.”
            </p>

            <p>
              You shrink so others feel comfortable — even when you’ve earned
              your shine.
            </p>

            <p className="text-[#578d48]">
              But dimming your light doesn’t make anyone else brighter.
              <br />
              It only trains your mind to associate success with guilt.
            </p>

            <p className="text-[#578d48]">
              ✨ Celebrate quietly or loudly, but don’t skip it.
              Self-acknowledgment builds inner strength.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. You Overthink Everything
            </h3>

            <p>
              You turn small decisions into full-blown mental wars.
              <br />
              You analyze every “what if” until you’re paralyzed.
            </p>

            <p className="text-[#578d48]">
              Overthinking is your mind’s way of pretending it’s productive
              while secretly avoiding action.
            </p>

            <p className="text-[#578d48]">
              ✨ Clarity doesn’t come from thinking more.
              It comes from moving, failing, adjusting.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. You Sabotage Good Things Because You “Don’t Deserve It”
            </h3>

            <p>
              When something beautiful arrives — love, opportunity, peace —
              <br />
              you start finding reasons to push it away.
              <br />
              You wait for the “catch.” You expect the fall.
            </p>

            <p className="text-[#578d48]">
              It’s not that you don’t deserve good things.
              <br />
              It’s that you’ve normalized pain so much, comfort feels foreign.
            </p>

            <p className="text-[#578d48]">
              ✨ You deserve softness without earning it through suffering.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. You Delay Starting Because It Won’t Be Perfect
            </h3>

            <p>
              You tell yourself, “I’ll begin when it’s ready.”
              <br />
              Translation: I’m scared to fail.
            </p>

            <p className="text-[#578d48]">
              Perfectionism isn’t a high standard — it’s disguised fear.
              <br />
              And it kills more dreams than failure ever will.
            </p>

            <p className="text-[#578d48]">
              ✨ Start ugly. Progress beats perfection every single time.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. You Stay in Toxic Environments Out of Fear
            </h3>

            <p>
              You call it “loyalty,” but deep down it’s fear of being alone.
              <br />
              You’d rather breathe stale air than face the unknown.
            </p>

            <p className="text-[#578d48]">
              But the truth is — staying where you’re drained is also a choice.
              <br />
              And not choosing change is still a decision.
            </p>

            <p className="text-[#578d48]">
              ✨ Sometimes peace costs your comfort zone.
              Pay it.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. You Compare Your Journey Constantly
            </h3>

            <p>
              You scroll. You see their wins. You shrink.
              <br />
              You forget that everyone’s highlight reel hides a struggle.
            </p>

            <p className="text-[#578d48]">
              Comparison steals presence.
              <br />
              You can’t grow roots when your attention is always elsewhere.
            </p>

            <p className="text-[#578d48]">
              ✨ Focus on your lane. Your timeline is sacred.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. You Don’t Allow Yourself to Rest — You Try to Earn It
            </h3>

            <p>
              You grind until burnout, then call it discipline.
              <br />
              You treat rest like a reward for pain — instead of a human need.
            </p>

            <p className="text-[#578d48]">
              But rest isn’t laziness. It’s maintenance.
              <br />
              Even the sun sets daily.
            </p>

            <p className="text-[#578d48]">
              ✨ Slow down before your body forces you to.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. You Speak to Yourself Like You’re Your Enemy
            </h3>

            <p>
              You encourage everyone else — but tear yourself apart inside.
              <br />
              You call it “motivation,” but it’s self-abuse.
            </p>

            <p className="text-[#578d48]">
              The words you say in your head shape your reality more than any
              external voice.
            </p>

            <p className="text-[#578d48]">
              ✨ If you wouldn’t say it to a friend, don’t say it to yourself.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. You Seek Validation From People Who Don’t Value You
            </h3>

            <p>
              You keep chasing approval from people who barely notice your
              worth.
              <br />
              You confuse attention with connection.
            </p>

            <p className="text-[#578d48]">
              You can’t feel seen by those who don’t even look deeply.
            </p>

            <p className="text-[#578d48]">
              ✨ The day you stop performing for others is the day you start
              living authentically.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              10. You Confuse Self-Sacrifice With Love
            </h3>

            <p>
              You think giving up your needs proves devotion.
              <br />
              You equate burnout with worthiness.
            </p>

            <p className="text-[#578d48]">
              But love isn’t about losing yourself — it’s about expanding with
              yourself.
            </p>

            <p className="text-[#578d48]">
              ✨ Real love nourishes both hearts, not just one.
            </p>
          </div>
        </section>

        {/* Truth Bomb */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            Truth Bomb 💥
          </h2>

          <p>
            Sometimes, the war you keep fighting isn’t with the world.
            <br />
            It’s with the part of you that still believes you’re not enough.
          </p>

          <p className="text-[#578d48]">
            But here’s the beauty —
            <br />
            The moment you become aware of your inner enemy,
            <br />
            you stop fighting yourself…
            <br />
            and start healing yourself.
          </p>

          <p className="text-[#578d48]">
            Because the same energy you use to sabotage your growth
            <br />
            can be used to build your peace.
          </p>
        </section>

        {/* Wisdom */}
        <section className="space-y-4 mb-10">
          <p className="text-[#578d48] font-semibold">Wisdom Lens:</p>

          <p className="text-[#578d48] italic">
            “You are not the voice of your doubt.
            <br />
            You are the awareness that hears it.”
            <br />— Unknown
          </p>

          <p className="text-[#578d48] font-semibold">Modern Meaning:</p>

          <p className="text-[#578d48]">
            Becoming your own ally is the first step toward freedom.
            <br />
            When your inner dialogue becomes gentle,
            <br />
            life starts feeling lighter — not because the world changed,
            <br />
            but because you did.
          </p>

          <p className="text-[#578d48] font-semibold">Journal Prompt:</p>

          <p className="text-[#578d48] italic">
            “What are 3 ways I’ve been standing in my own way — and how can I
            show up differently this week?”
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog18;
